import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

const isDefinedString = (value: unknown): value is string => {
  if (value == null) return false;
  if (typeof value !== 'string') return false;
  if (value.trim() === '') return false;
  return true;
};

export type SearchingOptions = {
  search?: string | null;
  ms?: number;
};

export const useSearching = (options?: SearchingOptions) => {
  const [search, setSearch] = useState<string | null>(options?.search ?? null);
  const [debouncing, setDebouncing] = useState<boolean>(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useDebounce(
    () => {
      setDebouncedSearch(search);
      setDebouncing(false);
    },
    options?.ms ?? 250,
    [search]
  );

  const updateSearch = useCallback(
    (value: string | null) => {
      if (search === value) return;
      setSearch(value);
      setDebouncing(true);
    },
    [search]
  );

  const clearSearch = useCallback(
    (value: string | null = null, debouncing = false) => {
      if (search === null) return;
      setSearch(value);
      setDebouncing(debouncing);
    },
    [search]
  );

  const hasSearch = useMemo(() => isDefinedString(search), [search]);

  return { search, debouncedSearch, updateSearch, clearSearch, hasSearch, searchDebouncing: debouncing };
};

export default useSearching;
